import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import MonacoEditor from "react-monaco-editor";
import { executeCode } from "../../services/pistonApi";
import "./CodeExecutor.css";
import {
  CardHeader,
  Button,
  Card,
  CardBody,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { ResizableBox } from 'react-resizable';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faChalkboard, faCode, faTerminal } from "@fortawesome/free-solid-svg-icons";
import 'react-resizable/css/styles.css';
import * as monaco from "monaco-editor"; // Import Monaco editor
import SplitPane from "react-split-pane";
import { useTranslation } from "react-i18next";
import { requestExecuteCode } from "../../services/userService"
import useBreakpoint from "../../hooks/useBreakpoint";

function CodeExecutor2({ languageCode, initialCode = "// Write your code here", runTestCase, setCurrentCode, setCurrentOuput, children }) {
  const [code, setCode] = useState(initialCode);
  const [output, setOutput] = useState(null);
  const { t, i18n } = useTranslation();

  // Responsive breakpoint flags. The 3-pane SplitPane layout is unusable
  // below lg (~992px), so we swap to a tabbed layout (Lesson | Code | Output).
  const { lgUp, isMobile } = useBreakpoint();

  // Mobile tab state. "lesson" is the default landing tab on small screens.
  const [activeMobileTab, setActiveMobileTab] = useState("lesson");

  // Keep just an initial default; let SplitPane manage live size during drag
  const [widths, setWidths] = useState(() => {
    const w = window.innerWidth || 1200;
    return [w / 3, w / 3, w / 3];
  });

  const language = languageCode;
  const editorRef = useRef(null);

  const handleChange = (newValue) => {
    setCode(newValue);
    setCurrentCode(newValue);
  }

  const runCode = async () => {
    try {
      // const result = await executeCode(language, code);
      const result = await requestExecuteCode(language, code);
      const rawOutput = result.stdout || result.stderr || t("promptLab.noOutput");
      setOutput(rawOutput);
      // AI reference
      setCurrentOuput(rawOutput);
      runTestCase(code, rawOutput);

      // After running, jump to the Output tab on mobile so users see results
      // without having to manually swipe over.
      if (!lgUp) setActiveMobileTab("output");
    } catch (error) {
      setOutput(t("promptLab.executionFailed", { msg: error?.message || String(error) }));
    }
  };

  // Editor options vary slightly by viewport — disabling the minimap on
  // small screens reclaims ~80px of horizontal space.
  const editorOptions = useMemo(() => ({
    selectOnLineNumbers: true,
    readOnly: false,
    fontSize: lgUp ? 14 : 13,
    wordWrap: "on",
    minimap: { enabled: lgUp },
    scrollBeyondLastLine: false,
    // Turn OFF automaticLayout on desktop where SplitPane manages it manually.
    // On mobile/tablet there's no SplitPane, so let Monaco self-layout
    // (the editor container changes size when switching tabs / rotating).
    automaticLayout: !lgUp,
    // Screen readers hear what this is and which language it holds.
    ariaLabel: t("promptLab.codeEditor", { lang: language }),
    accessibilitySupport: "auto",
  }), [lgUp, t, language]);

  // Debounced window resize -> layout editor + recompute SplitPane defaults.
  useEffect(() => {
    let t;
    const onWinResize = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        if (editorRef.current) editorRef.current.layout();
        // Recompute the default 1/3 widths so a rotation / resize past a
        // breakpoint produces a sensible SplitPane layout next time it mounts.
        const w = window.innerWidth || 1200;
        setWidths([w / 3, w / 3, w / 3]);
      }, 50);
    };
    window.addEventListener("resize", onWinResize);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onWinResize);
    };
  }, []);

  // When switching mobile tabs, give Monaco a tick to relayout against its
  // new container size — otherwise it can render at 0×0 the first time.
  useEffect(() => {
    if (!lgUp && activeMobileTab === "code" && editorRef.current) {
      const id = setTimeout(() => editorRef.current && editorRef.current.layout(), 50);
      return () => clearTimeout(id);
    }
  }, [activeMobileTab, lgUp]);

  // Monaco theme once
  useEffect(() => {
    monaco.editor.defineTheme("customTheme", {
      base: "vs-dark",
      inherit: true,
      rules: [],
      colors: { "editor.background": "#272727" },
    });
    monaco.editor.setTheme("customTheme");



  }, []);

  // Keep code in sync when initialCode changes
  useEffect(() => {
    setCode(initialCode);
    setCurrentCode(initialCode);
  }, [initialCode]);

  // Only commit widths AFTER drag finishes (reduces RO churn)
  const commitWidth = useCallback((index, size) => {
    setWidths((prev) => {
      const next = [...prev];
      next[index] = size;
      return next;
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Render helpers — defined once and reused in both the desktop SplitPane and
  // the mobile tabbed layout to avoid duplicating JSX.
  // ---------------------------------------------------------------------------
  const lessonPane = (
    <div className="fullWitdthHeight">{children}</div>
  );

  const editorPane = (
    <Card className="fullWitdthHeight transparent-card d-flex flex-column">
      <CardHeader className="grayBackground4 grayBorderB lesson-header code-toolbar">
        <Button onClick={runCode} className="run-code-btn">
          <FontAwesomeIcon icon={faPlay} className="run-code-icon" />
          <span>{t("regular.run")}</span>
        </Button>
      </CardHeader>

      <CardBody className="removeCardBodyPadding flex-grow-1">
        <div className="grayBackground5 fullWitdthHeight" style={{ height: "100%" }}>
          <MonacoEditor
            language={language}
            theme="vs-dark"
            value={code}
            onChange={handleChange}
            options={editorOptions}
            editorDidMount={(editor) => {
              editorRef.current = editor;
              setTimeout(() => editor.layout(), 0);
            }}
          />
        </div>
      </CardBody>
    </Card>
  );

  const outputPane = (
    <div className="grayBorderL output" style={{ width: "100%", height: "100%" }}>
      <pre className="card styleCard h-100 whiteText ">
        {/* <pre className="m-0 whiteText" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}> */}
        {output}
        {/* </pre> */}
      </pre>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Mobile / tablet: tabbed layout. Each pane gets its own dedicated screen
  // — far more usable than three squeezed columns at <992px.
  // ---------------------------------------------------------------------------
  if (!lgUp) {
    const setTab = (tab) => () => setActiveMobileTab(tab);
    // Use 60vh for tab content height — leaves room for nav + run button.
    const mobilePaneStyle = { height: "60vh", minHeight: "320px" };

    return (
      <Row id="myDiv" className="p-0 m-0 grayBorderB">
        <div className="splitpane-wrapper" style={{ width: "100%" }}>
          <Nav tabs className="nav-tabs-bottom justify-content-center">
            <NavItem>
              <NavLink
                className={classnames({ active: activeMobileTab === "lesson" })}
                onClick={setTab("lesson")}
                style={{ cursor: "pointer" }}
              >
                <FontAwesomeIcon icon={faChalkboard} />{" "}
                <span className="d-none d-sm-inline">{t("lesson.lesson") || "Lesson"}</span>
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeMobileTab === "code" })}
                onClick={setTab("code")}
                style={{ cursor: "pointer" }}
              >
                <FontAwesomeIcon icon={faCode} />{" "}
                <span className="d-none d-sm-inline">Code</span>
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeMobileTab === "output" })}
                onClick={setTab("output")}
                style={{ cursor: "pointer" }}
              >
                <FontAwesomeIcon icon={faTerminal} />{" "}
                <span className="d-none d-sm-inline">Output</span>
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeMobileTab} style={{ width: "100%" }}>
            <TabPane tabId="lesson" style={mobilePaneStyle}>
              <div style={{ height: "100%", overflow: "auto" }}>{lessonPane}</div>
            </TabPane>
            <TabPane tabId="code" style={mobilePaneStyle}>
              {editorPane}
            </TabPane>
            <TabPane tabId="output" style={mobilePaneStyle}>
              {outputPane}
            </TabPane>
          </TabContent>
        </div>
      </Row>
    );
  }

  // ---------------------------------------------------------------------------
  // Desktop (>=992px): original three-pane SplitPane layout.
  // ---------------------------------------------------------------------------
  return (
    <Row id="myDiv" className="p-0 m-0 grayBorderB  " >
      <div className="splitpane-wrapper ">
        <SplitPane
          split="vertical"
          minSize={100}
          defaultSize={widths[0]}
          onChange={() => requestAnimationFrame(() => editorRef.current && editorRef.current.layout())}
          onDragFinished={(size) => {
            commitWidth(0, size);
            setTimeout(() => editorRef.current && editorRef.current.layout(), 0);
          }}
        >
          {/* Left Pane */}
          {lessonPane}

          <SplitPane
            split="vertical"
            minSize={100}
            defaultSize={widths[1]}
            onChange={() => requestAnimationFrame(() => editorRef.current && editorRef.current.layout())}
            onDragFinished={(size) => {
              commitWidth(1, size);
              setTimeout(() => editorRef.current && editorRef.current.layout(), 0);
            }}
          >
            {/* Middle (Editor) Pane */}
            {editorPane}

            {/* Right (Output) Pane */}
            {outputPane}
          </SplitPane>
        </SplitPane>
      </div>
    </Row>
  );
}

export default CodeExecutor2;
